// @givmo/app-admin — whole-dollar money parsing/formatting for the onboarding wizard.
//
// ★ The match-policy endpoint takes money as WHOLE-DOLLAR INTEGERS (JSON numbers), NOT the
// decimal-strings used by the donation/grant surfaces. The masked input shows `$x,xxx`
// (thousands-separated, no decimals — Flutter `MoneyMaskedTextController(precision: 0)`); this
// module converts between the masked display string and the integer the wire needs.
//
// IMask config for the field: `mask: '$num'`, block `num: { mask: Number, thousandsSeparator: ',',
// scale: 0 }` — whole dollars only, so the integer parse is exact (no float rounding).

/** The IMask options for the whole-dollar money field (mirrors Flutter `precision: 0`). */
export const WHOLE_DOLLAR_MASK = {
  mask: '$num',
  blocks: {
    num: { mask: Number, thousandsSeparator: ',', scale: 0, signed: false },
  },
} as const;

/**
 * Parse a masked display string (`"$1,234"`, `"1,234"`, `""`) to a whole-dollar integer.
 * Returns `null` for empty/zero/invalid input (mirrors the Flutter `n > 0 ? n : null`). Because
 * the mask is decimal-less, `Number()` over the stripped digits is an exact integer — never a
 * float, never a decimal-string.
 */
export function parseWholeDollars(masked: string): number | null {
  const digits = masked.replace(/[^0-9]/g, '');
  if (digits.length === 0) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Format a whole-dollar integer back to the masked display string (`1234 → "$1,234"`). */
export function formatWholeDollars(value: number | null | undefined): string {
  if (value === null || value === undefined || value <= 0) return '';
  return `$${Math.trunc(value).toLocaleString('en-US')}`;
}
